import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useNotificationStore, type Toast } from '@/store/notificationStore';
import styles from './ui.module.css';

const icons: Record<Toast['type'], React.ReactNode> = {
  success: <CheckCircle size={16} style={{ color: '#10B981', flexShrink: 0, marginTop: 2 }} />,
  error: <AlertCircle size={16} style={{ color: '#EF4444', flexShrink: 0, marginTop: 2 }} />,
  warning: <AlertTriangle size={16} style={{ color: '#FB923C', flexShrink: 0, marginTop: 2 }} />,
  info: <Info size={16} style={{ color: '#3B82F6', flexShrink: 0, marginTop: 2 }} />,
};

const typeClass: Record<Toast['type'], string> = {
  success: styles.toastSuccess,
  error: styles.toastError,
  warning: styles.toastWarning,
  info: styles.toastInfo,
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  useEffect(() => {
    const duration = toast.duration ?? 5000;
    const timer = setTimeout(() => onRemove(toast.id), duration);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={[styles.toast, typeClass[toast.type]].join(' ')}
      role="alert"
      aria-live="assertive"
    >
      {icons[toast.type]}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p className={styles.toastTitle}>{toast.title}</p>
        {toast.description && <p className={styles.toastDescription}>{toast.description}</p>}
      </div>
      <button onClick={() => onRemove(toast.id)} className={styles.toastClose} aria-label="Dismiss">
        <X size={14} />
      </button>
    </motion.div>
  );
};

export const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useNotificationStore();

  if (toasts.length === 0) return null;

  return createPortal(
    <div className={styles.toastContainer} aria-label="Notifications" aria-live="polite">
      <AnimatePresence>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
};
